import React from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';
import { radius, useColors } from '../theme';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { T } from './Text';

export interface TabStripItem {
  id: string;
  label: string;
  icon?: IconName;
}

/**
 * A row of pill tabs inside a bordered track.
 *
 * Shared because the same overflow bug appeared in two of these independently:
 * a fixed row of pills with `alignSelf: 'flex-start'` is as wide as its
 * content, and content does not care how wide the phone is. The period tabs
 * measured 421px against a 375px screen and the menu's mode tabs 336px against
 * 320px in Gujarati — in both cases the last tab sat past the right edge where
 * it could be seen but never tapped.
 *
 * Below `wrapBelow` the strip stretches to the full width and the pills wrap,
 * growing to share the rows evenly. `alignSelf` has to change with it:
 * 'flex-start' would shrink the track back around a single row and undo the
 * wrap entirely.
 */
export function TabStrip({
  items,
  active,
  onChange,
  testIDPrefix,
  /** Widths under this wrap. 520 suits a 3–4 tab strip with short labels. */
  wrapBelow = 520,
  marginBottom = 16,
}: {
  items: TabStripItem[];
  active: string;
  onChange: (id: string) => void;
  testIDPrefix: string;
  wrapBelow?: number;
  marginBottom?: number;
}) {
  const colors = useColors();
  const { width } = useWindowDimensions();
  const narrow = width < wrapBelow;

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: narrow ? 'wrap' : 'nowrap',
        gap: 4,
        padding: 4,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.line,
        backgroundColor: colors.surface,
        alignSelf: narrow ? 'stretch' : 'flex-start',
        marginBottom,
      }}
    >
      {items.map((tab) => {
        const on = active === tab.id;
        return (
          <Pressable
            key={tab.id}
            onPress={() => onChange(tab.id)}
            testID={`${testIDPrefix}-${tab.id}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: tab.icon ? 7 : 0,
              paddingVertical: 8,
              // Wrapped pills give up a little side padding and grow instead,
              // so a row of two or three reads as even targets.
              paddingHorizontal: narrow ? 10 : 14,
              ...(narrow ? { flexGrow: 1 } : null),
              borderRadius: radius.sm,
              backgroundColor: on ? colors.gold : 'transparent',
            }}
          >
            {tab.icon ? (
              <Icon
                name={tab.icon}
                size={15}
                color={on ? colors.onGold : colors.textDim}
              />
            ) : null}
            <T size={13} weight={700} color={on ? colors.onGold : colors.textDim}>
              {tab.label}
            </T>
          </Pressable>
        );
      })}
    </View>
  );
}
